/**
 * Public URL Configuration
 * 
 * This module provides environment-aware URL generation for public-facing links
 * like Public Profile URLs.
 * 
 * Priority order:
 * 1. REACT_APP_PUBLIC_URL - Explicit public URL (for production custom domain)
 * 2. window.location.origin - Current browser origin (works in all environments)
 * 
 * For production deployment:
 * Set REACT_APP_PUBLIC_URL=https://genitradeapp.com (or your custom domain)
 * 
 * For preview/development:
 * Leave REACT_APP_PUBLIC_URL unset - will use current origin automatically
 */

// Get the public-facing base URL for the application
export const getPublicBaseUrl = () => {
  // Priority 1: Explicit public URL configured (production custom domain)
  if (process.env.REACT_APP_PUBLIC_URL) {
    // Remove trailing slash if present
    return process.env.REACT_APP_PUBLIC_URL.replace(/\/$/, '');
  }
  
  // Priority 2: Current browser origin (works for preview/dev environments)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  
  // Fallback: Derive from backend URL (last resort)
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) {
    return backendUrl.replace('/api', '').replace(/\/$/, '');
  }
  
  // Should never reach here in browser context
  return '';
};

// Generate Public Profile URL for a user
export const getPublicProfileUrl = (userId) => {
  if (!userId) return '';
  const baseUrl = getPublicBaseUrl();
  return `${baseUrl}/profile/${userId}`;
};

// Generate Vanity URL for a username
export const getVanityUrl = (username) => {
  if (!username) return '';
  const baseUrl = getPublicBaseUrl();
  return `${baseUrl}/u/${username}`;
};

// Get the best public profile URL (vanity if available, fallback to userId)
export const getBestPublicUrl = (userId, username) => {
  if (username) {
    return getVanityUrl(username);
  }
  return getPublicProfileUrl(userId);
};

// Check if we're using a production custom domain
export const isProductionDomain = () => {
  const publicUrl = process.env.REACT_APP_PUBLIC_URL;
  if (!publicUrl) return false;
  
  // Check if it's NOT an Emergent preview/host domain
  return !publicUrl.includes('emergentagent.com') && 
         !publicUrl.includes('emergent.host') &&
         !publicUrl.includes('localhost');
};

// Get display-friendly domain name (for UI hints)
export const getDisplayDomain = () => {
  const baseUrl = getPublicBaseUrl();
  try {
    const url = new URL(baseUrl);
    return url.hostname;
  } catch {
    return baseUrl;
  }
};

export default {
  getPublicBaseUrl,
  getPublicProfileUrl,
  getVanityUrl,
  getBestPublicUrl,
  isProductionDomain,
  getDisplayDomain
};
